// ============================================
// MockDetector Floating Bubble - Full Version
// ============================================
// Built by Silver (Anton Fredriksson) 🇸🇪
// This is the complete widget component
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, ChevronUp, ChevronDown, Play, Route, Download, Minimize2, GripVertical, Settings, Database, Sparkles, X, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

import { useMockDetection } from '../hooks/useMockDetection';
import { useMockDetectorSettings } from '../hooks/useMockDetectorSettings';
import { useDatabaseScanner } from '../hooks/useDatabaseScanner';
import { useBetaCredits } from '../hooks/useBetaCredits';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { useMockDetectorControl } from '../hooks/useMockDetectorControl';
import { useMockDetectorStorage } from '../hooks/useMockDetectorStorage';
import { MockDetectorSettings } from './MockDetectorSettings';
import { MockDetectorInfo } from './MockDetectorInfo';
import { 
  downloadCombinedReportAsTxt
} from '../utils/reportGenerator';
import type { DatabaseScanResult } from '../types/database.types';
import { useMultiLayerAccessControl } from '../hooks/useMultiLayerAccessControl';
import { toast } from '../hooks/use-toast';
import { getAllRoutes } from '../utils/projectSizeDetection';
import { WIDGET_VERSION } from '../utils/version';
import { useApiKeyValidation } from '../hooks/useApiKeyValidation';
import { DEFAULT_HIDDEN_ROUTES } from '../config/routes';

export interface MockDetectorFloatingBubbleProps {
  apiKey?: string;
  enableHeartbeat?: boolean;
  hideOnRoutes?: string[];
  storagePrefix?: string;
  validationEndpoint?: string;
  heartbeatEndpoint?: string;
}

export const MockDetectorFloatingBubble: React.FC<MockDetectorFloatingBubbleProps> = ({
  apiKey,
  enableHeartbeat = true,
  hideOnRoutes,
  storagePrefix = '',
  validationEndpoint,
  heartbeatEndpoint
}) => {
  const location = useLocation();
  const { 
    isScanning, 
    scanResult, 
    setScanResult, // ✅ FIX: Import setter for clearing results
    runScan, 
    runMultiRouteScan, 
    runSmartDeepScan, 
    isDeepScanning, 
    currentScanProgress,
    sourceFileProgress,
    filesScanned,
    totalFiles,
  } = useMockDetection();
  
  const { isMinimized, toggleMinimize, position, updatePosition } = useMockDetectorSettings(storagePrefix);
  
  // Force initial position to bottom-right
  useEffect(() => {
    const bubbleWidth = 400;
    const bubbleHeight = 600;
    const margin = 16;
    const headerHeight = 100;
    
    const bottomRightX = window.innerWidth - bubbleWidth - margin;
    const bottomRightY = window.innerHeight - bubbleHeight - margin;
    
    const bottomRightPos = { 
      x: Math.max(margin, bottomRightX), 
      y: Math.max(headerHeight, bottomRightY)
    };
    
    updatePosition(bottomRightPos);
  }, []);
  
  const { scanDatabase, isScanning: isDatabaseScanning, progress: databaseProgress } = useDatabaseScanner();
  const { canAccessModeratorFeatures } = useMultiLayerAccessControl();
  
  // API key validation
  const { 
    isValid: isApiKeyValid, 
    validationError, 
    dailyScanLimit: apiDailyScanLimit,
    isLoading: isValidatingApiKey,
    keyData
  } = useApiKeyValidation(apiKey, enableHeartbeat, validationEndpoint, heartbeatEndpoint);
  
  const { 
    requestScan,
    reportScanResult
  } = useBetaCredits(apiKey, apiDailyScanLimit as number | undefined, storagePrefix);
  
  const { 
    latestVersion,
    shouldShowNotification,
    dismissUpdate,
  } = useVersionCheck();
  
  const { isVisible, isPermanentlyDisabled, enable } = useMockDetectorControl(storagePrefix);
  const { loadLatestScan } = useMockDetectorStorage(storagePrefix);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [scanStatus, setScanStatus] = useState<string>('Ready to scan');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showSettingsInfo, setShowSettingsInfo] = useState(false);
  const [databaseScanResult, setDatabaseScanResult] = useState<DatabaseScanResult | null>(null);
  const [hiddenUntilRefresh, setHiddenUntilRefresh] = useState(false);
  const [locallyDisabled, setLocallyDisabled] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingScanType, setPendingScanType] = useState<'smart' | 'routes' | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Update scan status
  useEffect(() => {
    if (isScanning && currentScanProgress) {
      if (totalFiles > 0 && filesScanned > 0) {
        const progressPercent = Math.round(sourceFileProgress);
        setScanStatus(`Scanning file ${filesScanned}/${totalFiles} (${progressPercent}%)`);
      } else {
        setScanStatus(currentScanProgress);
      }
    } else if (isDatabaseScanning && databaseProgress) {
      setScanStatus(databaseProgress);
    } else if (scanResult) {
      const totalIssues = scanResult.totalViolations + (databaseScanResult?.totalViolations || 0);
      if (totalIssues === 0) {
        setScanStatus('✅ All clean!');
      } else {
        setScanStatus(`${totalIssues} issue${totalIssues > 1 ? 's' : ''} detected`);
      }
    } else {
      setScanStatus('Ready to scan');
    }
  }, [scanResult, databaseScanResult, isScanning, isDatabaseScanning, currentScanProgress, databaseProgress, sourceFileProgress, filesScanned, totalFiles]);

  // Auto-restore last scan
  useEffect(() => {
    if (!scanResult && !databaseScanResult && !isScanning && !isDeepScanning) {
      const saved = loadLatestScan();
      if (saved && 'scanType' in saved && saved.scanType === 'database') {
        setDatabaseScanResult(saved as DatabaseScanResult);
        toast({
          title: "Previous scan restored",
          description: "Database scan loaded successfully",
        });
      }
    }
  }, [scanResult, databaseScanResult, isScanning, isDeepScanning, loadLatestScan]);

  // Dragging logic
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!bubbleRef.current) return;
    const rect = bubbleRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;
      
      updatePosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, updatePosition]);

  // Emergency keyboard shortcut
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        enable();
        toast({
          title: "🚨 Emergency Re-enable",
          description: "MockDetector forced back on!",
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [enable]);

  // SECURITY: Route-based hiding (v1.2.2)
  const routesToHide = hideOnRoutes !== undefined ? hideOnRoutes : DEFAULT_HIDDEN_ROUTES;
  const isHiddenRoute = routesToHide.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  if (isHiddenRoute) {
    console.log('🚫 MockDetector hidden on route:', location.pathname);
    return null;
  }

  // Access control
  if (!canAccessModeratorFeatures) return null;

  // Show API key validation error if present
  if (apiKey && isApiKeyValid === false) {
    return (
      <div className="fixed bottom-4 right-4 z-50" data-mockdetector-ignore>
        <Card className="bg-gradient-to-br from-red-900/95 to-orange-900/95 border-red-500/50 backdrop-blur-sm shadow-lg max-w-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bug className="h-5 w-5 text-red-400" />
              <span className="text-white font-medium">MockDetector - API Key Error</span>
            </div>
            <p className="text-red-200 text-sm mb-3">{validationError || 'Invalid API key'}</p>
            <div className="text-xs text-red-300/70">
              Please check your API key or contact support at fredrikssonanton01@gmail.com
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while validating API key
  if (apiKey && isValidatingApiKey) {
    return (
      <div className="fixed bottom-4 right-4 z-50" data-mockdetector-ignore>
        <Card className="bg-gradient-to-br from-orange-900/95 to-red-900/95 border-orange-500/50 backdrop-blur-sm shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-400 animate-pulse" />
              <span className="text-white text-sm">Validating API key...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show re-enable card if disabled
  if (locallyDisabled || isPermanentlyDisabled) {
    return (
      <div className="fixed bottom-4 right-4 z-50" data-mockdetector-ignore>
        <Card className="bg-gradient-to-br from-green-900/95 to-emerald-900/95 border-green-500/50 backdrop-blur-sm shadow-lg">
          <CardContent className="p-2">
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-xs h-7 px-3"
              onClick={() => {
                setLocallyDisabled(false);
                enable();
                toast({ title: "✅ MockDetector Re-enabled" });
              }}
            >
              ✅ Re-enable MockDetector
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hiddenUntilRefresh || !isVisible) return null;

  // Scan handlers
  const handleScanDatabase = async () => {
    const scanType = 'DATABASE_SCAN';
    
    // ✅ FAS 2: Reset old results before new scan
    setScanResult(null);
    setDatabaseScanResult(null);
    setScanStatus('Starting database scan...');
    
    // Request scan from server
    const scanResponse = await requestScan(scanType);
    if (!scanResponse.success) {
      toast({ title: "Scan Failed", description: "Could not start scan", variant: "destructive" });
      return;
    }
    try {
      const result = await scanDatabase();
      setDatabaseScanResult(result);
      
      // Report scan result
      await reportScanResult(scanResponse.scanToken, result.totalViolations, scanType);
      
      toast({ title: "✅ Database Scan Complete" });
    } catch (error) {
      toast({ title: "Database Scan Failed", variant: "destructive" });
    }
  };

  const handleQuickScan = async () => {
    const scanType = 'QUICK_SCAN';
    
    // ✅ FAS 2: Reset old results before new scan
    setScanResult(null);
    setDatabaseScanResult(null);
    setScanStatus('Starting quick scan...');
    
    // Request scan from server (deducts credits)
    const scanResponse = await requestScan(scanType);
    if (!scanResponse.success) {
      toast({ title: "Scan Failed", description: "Could not start scan", variant: "destructive" });
      return;
    }
    try {
      await runScan();
      
      // Report scan result (refunds credits if no errors)
      if (scanResult) {
        await reportScanResult(scanResponse.scanToken, scanResult.totalViolations, scanType);
      }
      
      toast({ title: "✅ Quick Scan Complete" });
    } catch (error) {
      toast({ title: "Scan Failed", variant: "destructive" });
    }
  };

  const handleSmartDeepScan = async () => {
    const scanType = 'SMART_DEEP_SCAN';
    
    // ✅ FAS 2: Reset old results before new scan
    setScanResult(null);
    setDatabaseScanResult(null);
    setScanStatus('Starting smart deep scan...');
    
    // Request scan from server
    const scanResponse = await requestScan(scanType);
    if (!scanResponse.success) {
      toast({ title: "Scan Failed", description: "Could not start scan", variant: "destructive" });
      return;
    }
    try {
      await runSmartDeepScan();
      
      // Report scan result
      if (scanResult) {
        await reportScanResult(scanResponse.scanToken, scanResult.totalViolations, scanType);
      }
      
      toast({ title: "✅ Smart Deep Scan Complete" });
    } catch (error) {
      toast({ title: "Smart Deep Scan Failed", variant: "destructive" });
    }
  };

  const handleScanAllRoutes = async () => {
    const scanType = 'SCAN_ALL_ROUTES';
    
    // ✅ FAS 2: Reset old results before new scan
    setScanResult(null);
    setDatabaseScanResult(null);
    setScanStatus('Starting route scan...');
    
    // Request scan from server
    const scanResponse = await requestScan(scanType);
    if (!scanResponse.success) {
      toast({ title: "Scan Failed", description: "Could not start scan", variant: "destructive" });
      return;
    }
    
    const routes = getAllRoutes();
    try {
      await runMultiRouteScan(routes);
      
      // Report scan result
      if (scanResult) {
        await reportScanResult(scanResponse.scanToken, scanResult.totalViolations, scanType);
      }
      
      toast({ title: "✅ Multi-Route Scan Complete" });
    } catch (error) {
      toast({ title: "Multi-Route Scan Failed", variant: "destructive" });
    }
  };

  // ✅ FAS 4: Navigation warning handlers
  const handleSmartDeepScanWithWarning = () => {
    const hasSeenWarning = localStorage.getItem('mockdetector-navigation-warning-seen');
    
    if (!hasSeenWarning) {
      setPendingScanType('smart');
      setShowNavigationWarning(true);
    } else {
      handleSmartDeepScan();
    }
  };

  const handleScanAllRoutesWithWarning = () => {
    const hasSeenWarning = localStorage.getItem('mockdetector-navigation-warning-seen');
    
    if (!hasSeenWarning) {
      setPendingScanType('routes');
      setShowNavigationWarning(true);
    } else {
      handleScanAllRoutes();
    }
  };

  const confirmNavigationWarning = () => {
    localStorage.setItem('mockdetector-navigation-warning-seen', 'true');
    setShowNavigationWarning(false);
    
    if (pendingScanType === 'smart') {
      handleSmartDeepScan();
    } else if (pendingScanType === 'routes') {
      handleScanAllRoutes();
    }
    setPendingScanType(null);
  };

  const handleDownloadReport = () => {
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadCombinedReportAsTxt(scanResult, databaseScanResult, `mockdetector-report-${timestamp}.txt`);
    toast({ title: "✅ Report Downloaded", description: `Saved as mockdetector-report-${timestamp}.txt` });
  };

  const getSeverityColor = () => {
    if (!scanResult || scanResult.totalViolations === 0) return 'bg-green-500';
    if (scanResult.mockDataViolations.some(i => i.severity === 'critical' || i.severity === 'high')) return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  // Minimized view
  if (isMinimized) {
    return (
      <div 
        ref={bubbleRef}
        className="fixed z-50 transition-all duration-300 select-none"
        style={{ left: `${position.x}px`, top: `${position.y}px`, cursor: isDragging ? 'grabbing' : 'grab' }}
        data-mockdetector-ignore
      >
        <Card 
          className={`${
            isScanning || isDeepScanning 
              ? 'bg-gradient-to-br from-blue-900/95 to-purple-900/95 animate-pulse border-blue-500/50'
              : 'bg-gradient-to-br from-orange-900/95 to-red-900/95 border-orange-500/50'
          } backdrop-blur-sm shadow-2xl hover:scale-105 transition-transform`}
          onMouseDown={handleMouseDown}
        >
          <CardContent className="p-2 select-none">
            <div className="flex items-center gap-2 cursor-pointer" onClick={toggleMinimize}>
              <div className="relative">
                <Bug className={`h-5 w-5 ${
                  isScanning || isDeepScanning ? 'text-blue-400 animate-spin' : 'text-orange-400'
                }`} />
                {shouldShowNotification && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                )}
                {scanResult && scanResult.totalViolations > 0 && (
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 ${getSeverityColor()} rounded-full animate-pulse`} />
                )}
              </div>
              {scanResult && scanResult.totalViolations > 0 && (
                <Badge className={`${getSeverityColor()} text-white text-xs px-1.5 py-0.5`}>
                  {scanResult.totalViolations}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expanded view
  return (
    <div 
      ref={bubbleRef}
      className="fixed z-50 transition-all duration-300 select-none"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      data-mockdetector-ignore
    >
      <Card className="bg-gradient-to-br from-orange-900/95 to-red-900/95 border-orange-500/50 backdrop-blur-sm shadow-2xl max-w-sm">
        <CardContent className="p-3 select-none">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div 
              className="flex items-center gap-2 cursor-move flex-1"
              onMouseDown={handleMouseDown}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <GripVertical className="h-5 w-5 text-orange-400" />
              <Bug className="h-5 w-5 text-orange-400" />
              <div>
                <div className="text-white font-medium text-sm">
                  MockDetector Pro {apiKey && keyData && (
                    <span className="text-orange-300 text-[10px]">({keyData.tier})</span>
                  )}
                </div>
                <div className="text-orange-300 text-xs flex items-center gap-1">
                  {isScanning || isDeepScanning ? (
                    <>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                      {scanStatus}
                    </>
                  ) : (
                    scanStatus
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setShowSettingsInfo(true)}
                className="hover:bg-orange-500/20 group relative"
                title="Settings & Info"
              >
                <div className="flex items-center gap-1">
                  <Settings className="h-3.5 w-3.5 text-orange-300 group-hover:text-orange-200 transition-colors" />
                  <span className="text-orange-300/60 group-hover:text-orange-200/60 text-xs">/</span>
                  <Info className="h-3.5 w-3.5 text-orange-300 group-hover:text-orange-200 transition-colors" />
                </div>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={toggleMinimize}>
                <Minimize2 className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="mt-3 space-y-3 border-t border-orange-500/30 pt-3">
              {/* Update notification - Read-only */}
              {shouldShowNotification && latestVersion && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      ✨ New version v{latestVersion} detected! Auto-updating in background...
                    </p>
                    <Button size="sm" variant="ghost" onClick={dismissUpdate} className="h-5 w-5 p-0">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Scan Results Summary */}
              {((scanResult && scanResult.totalViolations > 0) || (databaseScanResult && databaseScanResult.totalViolations > 0)) && (
                <div className="bg-gradient-to-r from-orange-900/40 to-red-900/40 border border-orange-500/40 rounded p-2.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-orange-200">
                      🔍 Scan Results
                    </span>
                    <Badge className={`${getSeverityColor()} text-white text-[10px] px-1.5 py-0.5`}>
                      {(() => {
                        const total = (scanResult?.totalViolations || 0) + (databaseScanResult?.totalViolations || 0);
                        return total > 10 ? '10+' : total;
                      })()} issues
                    </Badge>
                  </div>
                  
                  {/* Severity Breakdown */}
                  <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                    {(() => {
                      const allViolations = [
                        ...(scanResult?.mockDataViolations || []),
                        ...(databaseScanResult?.violations || [])
                      ];
                      const critical = allViolations.filter(v => v.severity === 'critical').length;
                      const high = allViolations.filter(v => v.severity === 'high').length;
                      const medium = allViolations.filter(v => v.severity === 'medium').length;
                      const low = allViolations.filter(v => v.severity === 'low').length;
                      
                      return (
                        <>
                          {critical > 0 && (
                            <div className="flex items-center gap-1 bg-red-900/30 rounded px-1.5 py-1">
                              <div className="w-2 h-2 rounded-full bg-red-500" />
                              <span className="text-red-200">{critical} Critical</span>
                            </div>
                          )}
                          {high > 0 && (
                            <div className="flex items-center gap-1 bg-orange-900/30 rounded px-1.5 py-1">
                              <div className="w-2 h-2 rounded-full bg-orange-500" />
                              <span className="text-orange-200">{high} High</span>
                            </div>
                          )}
                          {medium > 0 && (
                            <div className="flex items-center gap-1 bg-yellow-900/30 rounded px-1.5 py-1">
                              <div className="w-2 h-2 rounded-full bg-yellow-500" />
                              <span className="text-yellow-200">{medium} Medium</span>
                            </div>
                          )}
                          {low > 0 && (
                            <div className="flex items-center gap-1 bg-blue-900/30 rounded px-1.5 py-1">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              <span className="text-blue-200">{low} Low</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Quick tip */}
                  <p className="text-[9px] text-orange-300/70 italic">
                    ⚠️ Showing first {Math.min((scanResult?.totalViolations || 0) + (databaseScanResult?.totalViolations || 0), 10)} issues - Download full report below
                  </p>
                </div>
              )}

              {/* Scan progress */}
              {isDeepScanning && totalFiles > 0 && (
                <div className="bg-orange-900/30 rounded p-2">
                  <div className="text-xs">Scanning: {filesScanned}/{totalFiles}</div>
                  <Progress value={sourceFileProgress} className="h-2 mt-1" />
                </div>
              )}

              {/* Scan buttons */}
              <div className="space-y-2">
                <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700" onClick={handleQuickScan} disabled={isScanning || isDatabaseScanning}>
                  <Play className="h-3 w-3 mr-1" />Quick Scan This Page
                </Button>
                <Button size="sm" className="w-full bg-yellow-600 hover:bg-yellow-700" onClick={handleSmartDeepScanWithWarning} disabled={isScanning || isDatabaseScanning}>
                  <Sparkles className="h-3 w-3 mr-1" />Smart Deep Scan
                </Button>
                <Button size="sm" className="w-full bg-red-600 hover:bg-red-700" onClick={handleScanAllRoutesWithWarning} disabled={isScanning || isDatabaseScanning}>
                  <Route className="h-3 w-3 mr-1" />Scan All Routes
                </Button>
                <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700" onClick={handleScanDatabase} disabled={isScanning || isDatabaseScanning}>
                  <Database className="h-3 w-3 mr-1" />Scan Database
                </Button>

                {/* Report export */}
                {((scanResult && scanResult.totalViolations > 0) || (databaseScanResult && databaseScanResult.totalViolations > 0)) && (
                  <div className="border-t border-orange-500/30 pt-2">
                    <Button size="sm" className="w-full bg-green-600 hover:bg-green-700" onClick={handleDownloadReport}>
                      <Download className="h-3 w-3 mr-1" />📥 Download Full Report (.txt)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Warning Dialog - FAS 4 */}
      <Dialog open={showNavigationWarning} onOpenChange={setShowNavigationWarning}>
        <DialogContent className="max-w-md bg-gradient-to-br from-blue-900/98 to-purple-900/98 border-blue-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-100">
              🔍 Route Scanning Notice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-blue-50">
            <p>
              This scan will briefly navigate between your app's routes to analyze them for mock data.
            </p>
            <div className="bg-blue-500/20 border border-blue-400/30 rounded p-3 space-y-1">
              <p className="text-blue-100">
                ✓ Your current page will be restored after scanning
              </p>
              <p className="text-blue-100">
                ✓ This happens in the background
              </p>
              <p className="text-blue-100">
                ✓ Takes ~30-60 seconds for most apps
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={confirmNavigationWarning} 
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Got it, Start Scan
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowNavigationWarning(false)}
                className="border-blue-400/50 text-blue-100 hover:bg-blue-500/20"
              >
                Cancel
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-blue-200/80 cursor-pointer">
              <input 
                type="checkbox" 
                className="rounded"
                onChange={(e) => {
                  if (e.target.checked) {
                    localStorage.setItem('mockdetector-navigation-warning-seen', 'true');
                  }
                }} 
              />
              Don't show this again
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog - FIXED: Proper sizing and scrolling */}
      <Dialog open={showSettingsInfo} onOpenChange={setShowSettingsInfo}>
        <DialogContent className="bg-gradient-to-br from-orange-900/98 to-red-900/98 border-orange-500/50 text-white max-w-2xl h-[90vh] max-h-[600px] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle className="text-orange-100 flex items-center gap-2">
              <Bug className="h-5 w-5" />MockDetector v{WIDGET_VERSION}
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="settings" className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <TabsList className="grid grid-cols-2 mx-6 mb-2 flex-shrink-0">
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="info">Info</TabsTrigger>
            </TabsList>
            <TabsContent value="settings" className="flex-1 mt-0 overflow-y-auto px-6 pb-6">
              <div className="space-y-4">
                <MockDetectorSettings
                  onClose={() => setShowSettingsInfo(false)}
                  onHide={() => { setHiddenUntilRefresh(true); setShowSettingsInfo(false); }}
                  onDisable={() => { setLocallyDisabled(true); setShowSettingsInfo(false); }}
                />
              </div>
            </TabsContent>
            <TabsContent value="info" className="flex-1 mt-0 overflow-y-auto px-6 pb-6">
              <div className="space-y-4">
                <MockDetectorInfo />
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};
